import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { useToast } from "../lib/toast";
import {
  type ControlCase,
  type ControlCaseDetail,
  type ControlObject,
  type ControlPackage,
  type DecisionItem,
  type IntakeItem,
  type KpiDefinition,
  type PackageType,
  useAdoptControlPackage,
  useControlCase,
  useControlCases,
  useControlContract,
  useControlDecisions,
  useControlIntake,
  useControlKpis,
  useControlOverview,
  useControlPackages,
  useCreateControlCase,
  useCreateReferenceCase,
  useDecideControlCase,
  useDeriveControlIntake,
  useMeasureControlImpact,
  useRunControlCase,
  useSaveControlKpi,
  useUpdateControlDecision,
  useUpdateControlIntake,
} from "../lib/controlSystem";
import "../styles.control-system.css";

type Tab = "today" | "cases" | "packages" | "kpis" | "setup";

const TAB_LABEL: Record<Tab, string> = {
  today: "Entscheidungen",
  cases: "Steuerungsfälle",
  packages: "Fachpakete",
  kpis: "KPI-Definitionen",
  setup: "Einrichtung",
};

const PACKAGE_LABEL: Record<PackageType, string> = {
  industry: "Branche",
  process: "Prozess",
  kpi: "Kennzahl",
  use_case: "Anwendungsfall",
};

const MODE_LABEL: Record<string, string> = {
  automatic: "Automatisch ermittelt",
  suggested: "Vorschlag",
  human_decision: "Menschliche Entscheidung",
};

const OBJECT_LABEL: Record<string, string> = {
  unternehmenskontext: "Kontext",
  datenstand: "Daten",
  messwert: "Messwert",
  auffaelligkeit: "Auffälligkeit",
  ursachenvermutung: "Ursache",
  potenzial: "Potenzial",
  business_case: "Business Case",
  compliance_vorpruefung: "Risiko",
  entscheidung: "Entscheidung",
  massnahme: "Maßnahme",
  wirkung: "Wirkung",
  lernergebnis: "Lernen",
};

function splitList(value: string): string[] {
  return value
    .split(/[\n,;]/)
    .map((part) => part.trim())
    .filter((part, index, all) => Boolean(part) && all.indexOf(part) === index);
}

function numberOrUndefined(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDate(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function confidence(value?: number): string {
  return `${Math.round((value ?? 0) * 100)} %`;
}

function errorText(error: unknown): string {
  if (!error || typeof error !== "object") return "Unbekannter Fehler";
  const item = error as { message?: string; details?: unknown };
  if (item.details && typeof item.details === "object") {
    const details = item.details as { detail?: unknown; error?: unknown };
    if (typeof details.detail === "string") return details.detail;
    if (details.detail && typeof details.detail === "object") {
      const inner = details.detail as { message?: unknown };
      if (typeof inner.message === "string") return inner.message;
    }
  }
  return item.message ?? "Aktion fehlgeschlagen";
}

function displayValue(value: unknown): string {
  if (value == null || value === "") return "-";
  if (Array.isArray(value)) return value.map(displayValue).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  return String(value);
}

function Status({ value, tone }: { value: string; tone?: string }) {
  const normalized = tone ?? value.toLowerCase().replace(/[^a-z0-9äöüß]+/g, "-");
  return <span className={`cs-status cs-status-${normalized}`}>{value.replace(/_/g, " ")}</span>;
}

function Evidence({ refs }: { refs?: string[] }) {
  const count = refs?.length ?? 0;
  return (
    <span className={`cs-evidence ${count === 0 ? "missing" : ""}`} title={refs?.join("\n") || "Kein Nachweis hinterlegt"}>
      {count === 0 ? "Nachweis fehlt" : `${count} Nachweis${count === 1 ? "" : "e"}`}
    </span>
  );
}

function LoadingState({ label = "Daten werden geladen" }: { label?: string }) {
  return <div className="cs-empty" role="status">{label}...</div>;
}

function EmptyState({ children }: { children: string }) {
  return <div className="cs-empty">{children}</div>;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="cs-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="cs-modal" role="dialog" aria-modal="true" aria-label={title}>
        <header className="cs-modal-head">
          <h2>{title}</h2>
          <button type="button" className="cs-icon-btn" onClick={onClose} aria-label="Schließen" title="Schließen">×</button>
        </header>
        {children}
      </section>
    </div>
  );
}

function StageStrip({ detail, stages }: { detail: ControlCaseDetail; stages: Array<{ key: string; label: string }> }) {
  return (
    <ol className="cs-stages" aria-label="Steuerungskreislauf">
      {stages.map((stage) => {
        const count = detail.stage_counts?.[stage.key] ?? 0;
        const current = detail.current_stage === stage.key;
        return (
          <li key={stage.key} className={`${count > 0 ? "done" : ""} ${current ? "current" : ""}`}>
            <span aria-hidden>{count > 0 ? "✓" : ""}</span>
            <strong>{stage.label}</strong>
            {count > 1 && <small>{count}</small>}
          </li>
        );
      })}
    </ol>
  );
}

function DecisionRow({
  item,
  onOpen,
  canWrite,
}: {
  item: DecisionItem;
  onOpen: (caseId: string) => void;
  canWrite: boolean;
}) {
  const update = useUpdateControlDecision();
  const toast = useToast();

  function claim() {
    update.mutate(
      { id: item.id, input: { status: "in_pruefung" } },
      {
        onSuccess: () => toast.push({ kind: "success", title: "Prüfung übernommen" }),
        onError: (error) => toast.push({ kind: "error", title: "Übernahme fehlgeschlagen", body: errorText(error) }),
      },
    );
  }

  return (
    <tr>
      <td><Status value={item.priority} tone={item.priority} /></td>
      <td>
        <strong>{item.title}</strong>
        <p>{item.reason}</p>
      </td>
      <td>
        <span>{item.owner || "Nicht zugewiesen"}</span>
        <small>{confidence(item.confidence)} Verlässlichkeit</small>
      </td>
      <td><Evidence refs={item.evidence_refs} /></td>
      <td className="cs-table-actions">
        {canWrite && item.status === "offen" && (
          <button type="button" className="btn ghost small" onClick={claim} disabled={update.isPending}>Übernehmen</button>
        )}
        <button type="button" className="btn small" onClick={() => onOpen(item.case_id)}>Fall öffnen</button>
      </td>
    </tr>
  );
}

type CaseFormState = {
  title: string;
  owner: string;
  sourceRef: string;
  evidence: string;
  kpiId: string;
  packageIds: string[];
  actual: string;
  target: string;
  period: string;
  dimension: string;
  dataQuality: string;
  cause: string;
  causeConfidence: string;
  opportunity: string;
  annualVolume: string;
  minutesSaved: string;
  hourlyCost: string;
  automationRate: string;
  annualCost: string;
  oneOffCost: string;
  assumptionConfidence: string;
  personalData: boolean;
  automatedDecision: boolean;
  safetyCritical: boolean;
  externalModel: boolean;
};

const EMPTY_CASE: CaseFormState = {
  title: "",
  owner: "",
  sourceRef: "",
  evidence: "",
  kpiId: "",
  packageIds: [],
  actual: "",
  target: "",
  period: "",
  dimension: "",
  dataQuality: "",
  cause: "",
  causeConfidence: "0.70",
  opportunity: "",
  annualVolume: "",
  minutesSaved: "",
  hourlyCost: "",
  automationRate: "",
  annualCost: "",
  oneOffCost: "",
  assumptionConfidence: "0.70",
  personalData: false,
  automatedDecision: false,
  safetyCritical: false,
  externalModel: false,
};

function preferredPackageIds(packages: ControlPackage[]): string[] {
  return (Object.keys(PACKAGE_LABEL) as PackageType[])
    .map((type) => {
      const candidates = packages.filter((item) => item.package_type === type);
      return candidates.find((item) => item.layer === "kunde")?.id ?? candidates[0]?.id;
    })
    .filter((id): id is string => Boolean(id));
}

function CaseModal({
  packages,
  kpis,
  onClose,
  onCreated,
}: {
  packages: ControlPackage[];
  kpis: KpiDefinition[];
  onClose: () => void;
  onCreated: (caseId: string) => void;
}) {
  const [form, setForm] = useState<CaseFormState>(() => ({
    ...EMPTY_CASE,
    kpiId: kpis.find((item) => item.layer === "kunde")?.id ?? kpis[0]?.id ?? "",
    packageIds: preferredPackageIds(packages),
  }));
  const createCase = useCreateControlCase();
  const runCase = useRunControlCase();
  const toast = useToast();
  const selectedKpi = kpis.find((item) => item.id === form.kpiId);

  useEffect(() => {
    if (!form.target && selectedKpi?.target?.value != null) {
      setForm((current) => ({ ...current, target: String(selectedKpi.target.value) }));
    }
  }, [form.target, selectedKpi?.id, selectedKpi?.target?.value]);

  function set<K extends keyof CaseFormState>(key: K, value: CaseFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function togglePackage(id: string) {
    set("packageIds", form.packageIds.includes(id) ? form.packageIds.filter((value) => value !== id) : [...form.packageIds, id]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const evidence = splitList(form.evidence);
    if (!form.title.trim() || !form.kpiId || numberOrUndefined(form.actual) == null || !form.owner.trim()) {
      toast.push({ kind: "warn", title: "Pflichtangaben fehlen", body: "Titel, KPI, Messwert und Verantwortlicher sind erforderlich." });
      return;
    }
    if (!form.sourceRef.trim() || evidence.length === 0) {
      toast.push({ kind: "warn", title: "Nachweis fehlt", body: "Datenquelle und mindestens ein prüfbarer Nachweis sind erforderlich." });
      return;
    }
    try {
      const created = await createCase.mutateAsync({
        title: form.title.trim(),
        package_ids: form.packageIds,
        kpi_definition_id: form.kpiId,
        source_ref: form.sourceRef.trim(),
        owner: form.owner.trim(),
        evidence_refs: evidence,
        confidence: numberOrUndefined(form.dataQuality) ?? 0.7,
        context: {
          affected_dimension: form.dimension.trim(),
          data_quality: numberOrUndefined(form.dataQuality),
          measurement: {
            value: numberOrUndefined(form.actual),
            target: numberOrUndefined(form.target),
            period: form.period.trim(),
            unit: selectedKpi?.unit,
            evidence_refs: evidence,
          },
          cause: form.cause.trim(),
          cause_confidence: numberOrUndefined(form.causeConfidence),
          opportunity: form.opportunity.trim(),
          business_case: {
            annual_volume: numberOrUndefined(form.annualVolume),
            minutes_saved_per_unit: numberOrUndefined(form.minutesSaved),
            hourly_cost: numberOrUndefined(form.hourlyCost),
            automation_rate: numberOrUndefined(form.automationRate),
            annual_solution_cost: numberOrUndefined(form.annualCost),
            one_off_cost: numberOrUndefined(form.oneOffCost),
            assumption_confidence: numberOrUndefined(form.assumptionConfidence),
          },
          compliance: {
            personal_data: form.personalData,
            automated_decision: form.automatedDecision,
            safety_critical: form.safetyCritical,
            external_model: form.externalModel,
          },
        },
      });
      const evaluated = await runCase.mutateAsync(created.id);
      toast.push({
        kind: evaluated.status === "entscheidung_noetig" ? "info" : "success",
        title: "Steuerungsfall geprüft",
        body: evaluated.status.replace(/_/g, " "),
      });
      onCreated(created.id);
      onClose();
    } catch (error) {
      toast.push({ kind: "error", title: "Steuerungsfall konnte nicht angelegt werden", body: errorText(error) });
    }
  }

  const busy = createCase.isPending || runCase.isPending;
  return (
    <Modal title="Steuerungsfall anlegen" onClose={onClose}>
      <form className="cs-form" onSubmit={submit}>
        <fieldset>
          <legend>Kontext und Verantwortung</legend>
          <div className="cs-form-grid cols-2">
            <label><span>Titel *</span><input value={form.title} onChange={(e) => set("title", e.target.value)} /></label>
            <label><span>Verantwortlicher *</span><input value={form.owner} onChange={(e) => set("owner", e.target.value)} /></label>
            <label><span>Datenquelle *</span><input value={form.sourceRef} onChange={(e) => set("sourceRef", e.target.value)} placeholder="source://..." /></label>
            <label><span>Nachweise *</span><input value={form.evidence} onChange={(e) => set("evidence", e.target.value)} placeholder="evidence://..." /></label>
          </div>
        </fieldset>

        <fieldset>
          <legend>Kennzahl und Messung</legend>
          <div className="cs-form-grid cols-3">
            <label className="span-2"><span>KPI *</span><select value={form.kpiId} onChange={(e) => { set("kpiId", e.target.value); set("target", ""); }}>
              <option value="">Bitte wählen</option>
              {kpis.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.completeness.score_pct} %</option>)}
            </select></label>
            <label><span>Datenqualität 0-1</span><input inputMode="decimal" value={form.dataQuality} onChange={(e) => set("dataQuality", e.target.value)} /></label>
            <label><span>Istwert *</span><input inputMode="decimal" value={form.actual} onChange={(e) => set("actual", e.target.value)} /></label>
            <label><span>Zielwert</span><input inputMode="decimal" value={form.target} onChange={(e) => set("target", e.target.value)} /></label>
            <label><span>Zeitraum</span><input value={form.period} onChange={(e) => set("period", e.target.value)} placeholder="Q2 2026" /></label>
            <label className="span-3"><span>Betroffene Dimension</span><input value={form.dimension} onChange={(e) => set("dimension", e.target.value)} /></label>
          </div>
        </fieldset>

        <fieldset>
          <legend>Ursache und Potenzial</legend>
          <div className="cs-form-grid cols-3">
            <label className="span-2"><span>Ursachenvermutung</span><input value={form.cause} onChange={(e) => set("cause", e.target.value)} /></label>
            <label><span>Verlässlichkeit 0-1</span><input inputMode="decimal" value={form.causeConfidence} onChange={(e) => set("causeConfidence", e.target.value)} /></label>
            <label className="span-3"><span>Potenzial</span><input value={form.opportunity} onChange={(e) => set("opportunity", e.target.value)} /></label>
          </div>
        </fieldset>

        <fieldset>
          <legend>Business Case</legend>
          <div className="cs-form-grid cols-3">
            <label><span>Volumen pro Jahr</span><input inputMode="decimal" value={form.annualVolume} onChange={(e) => set("annualVolume", e.target.value)} /></label>
            <label><span>Minuten je Vorgang</span><input inputMode="decimal" value={form.minutesSaved} onChange={(e) => set("minutesSaved", e.target.value)} /></label>
            <label><span>Stundensatz</span><input inputMode="decimal" value={form.hourlyCost} onChange={(e) => set("hourlyCost", e.target.value)} /></label>
            <label><span>Automatisierungsquote 0-1</span><input inputMode="decimal" value={form.automationRate} onChange={(e) => set("automationRate", e.target.value)} /></label>
            <label><span>Laufende Kosten pro Jahr</span><input inputMode="decimal" value={form.annualCost} onChange={(e) => set("annualCost", e.target.value)} /></label>
            <label><span>Einmalkosten</span><input inputMode="decimal" value={form.oneOffCost} onChange={(e) => set("oneOffCost", e.target.value)} /></label>
            <label><span>Annahmen-Verlässlichkeit 0-1</span><input inputMode="decimal" value={form.assumptionConfidence} onChange={(e) => set("assumptionConfidence", e.target.value)} /></label>
          </div>
        </fieldset>

        <fieldset>
          <legend>Risiko-Vorprüfung</legend>
          <div className="cs-check-grid">
            <label><input type="checkbox" checked={form.personalData} onChange={(e) => set("personalData", e.target.checked)} /> Personenbezogene Daten</label>
            <label><input type="checkbox" checked={form.automatedDecision} onChange={(e) => set("automatedDecision", e.target.checked)} /> Automatisierte Entscheidung</label>
            <label><input type="checkbox" checked={form.safetyCritical} onChange={(e) => set("safetyCritical", e.target.checked)} /> Sicherheitskritischer Kontext</label>
            <label><input type="checkbox" checked={form.externalModel} onChange={(e) => set("externalModel", e.target.checked)} /> Externes Modell</label>
          </div>
        </fieldset>

        <fieldset>
          <legend>Fachpakete</legend>
          <div className="cs-package-checks">
            {packages.map((item) => (
              <label key={item.id}>
                <input type="checkbox" checked={form.packageIds.includes(item.id)} onChange={() => togglePackage(item.id)} />
                <span><strong>{PACKAGE_LABEL[item.package_type]}</strong>{item.title}<small>{item.layer}</small></span>
              </label>
            ))}
          </div>
        </fieldset>

        <footer className="cs-form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn primary" disabled={busy}>{busy ? "Prüfung läuft..." : "Anlegen und prüfen"}</button>
        </footer>
      </form>
    </Modal>
  );
}

type KpiForm = {
  id?: string;
  layer: "fachwissen" | "kunde";
  metricKey: string;
  name: string;
  definition: string;
  formula: string;
  unit: string;
  period: string;
  direction: KpiDefinition["direction"];
  dimensions: string;
  requiredData: string;
  sourceRefs: string;
  target: string;
  tolerance: string;
  warning: string;
  critical: string;
  benchmark: string;
  benchmarkSource: string;
  owner: string;
  evidence: string;
  confidence: string;
  status: string;
  version: number;
};

function kpiFormFrom(item?: KpiDefinition | null): KpiForm {
  return {
    id: item?.id,
    layer: item?.layer ?? "kunde",
    metricKey: item?.metric_key ?? "",
    name: item?.name ?? "",
    definition: item?.definition ?? "",
    formula: item?.formula ?? "",
    unit: item?.unit ?? "",
    period: item?.period ?? "",
    direction: item?.direction ?? "neutral",
    dimensions: item?.dimensions?.join(", ") ?? "",
    requiredData: item?.required_data?.join(", ") ?? "",
    sourceRefs: item?.source_refs?.join(", ") ?? "",
    target: item?.target?.value != null ? String(item.target.value) : "",
    tolerance: item?.target?.tolerance_pct != null ? String(item.target.tolerance_pct) : "",
    warning: item?.warning_thresholds?.warning_pct != null ? String(item.warning_thresholds.warning_pct) : "",
    critical: item?.warning_thresholds?.critical_pct != null ? String(item.warning_thresholds.critical_pct) : "",
    benchmark: item?.benchmark?.value != null ? String(item.benchmark.value) : "",
    benchmarkSource: item?.benchmark?.source ?? "",
    owner: item?.owner ?? "",
    evidence: item?.evidence_refs?.join("\n") ?? "",
    confidence: item?.confidence != null ? String(item.confidence) : "0.70",
    status: item?.status ?? "entwurf",
    version: item?.version ?? 1,
  };
}

function KpiModal({ item, onClose }: { item?: KpiDefinition | null; onClose: () => void }) {
  const [form, setForm] = useState<KpiForm>(() => kpiFormFrom(item));
  const save = useSaveControlKpi();
  const toast = useToast();

  function set<K extends keyof KpiForm>(key: K, value: KpiForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const evidence = splitList(form.evidence);
    if (!form.metricKey.trim() || !form.name.trim() || !form.owner.trim() || evidence.length === 0) {
      toast.push({ kind: "warn", title: "Pflichtangaben fehlen", body: "Schlüssel, Name, Verantwortlicher und Nachweis sind erforderlich." });
      return;
    }
    save.mutate(
      {
        id: form.id,
        layer: form.layer,
        metric_key: form.metricKey.trim(),
        name: form.name.trim(),
        definition: form.definition.trim(),
        formula: form.formula.trim(),
        unit: form.unit.trim(),
        period: form.period.trim(),
        direction: form.direction,
        dimensions: splitList(form.dimensions),
        required_data: splitList(form.requiredData),
        source_refs: splitList(form.sourceRefs),
        target: { value: numberOrUndefined(form.target), tolerance_pct: numberOrUndefined(form.tolerance) },
        warning_thresholds: { warning_pct: numberOrUndefined(form.warning), critical_pct: numberOrUndefined(form.critical) },
        benchmark: { value: numberOrUndefined(form.benchmark), unit: form.unit.trim(), source: form.benchmarkSource.trim() },
        owner: form.owner.trim(),
        evidence_refs: evidence,
        confidence: numberOrUndefined(form.confidence),
        status: form.status,
        version: item ? form.version + 1 : form.version,
      },
      {
        onSuccess: (result) => {
          toast.push({
            kind: result.completeness.complete ? "success" : "warn",
            title: "KPI-Definition gespeichert",
            body: `${result.completeness.score_pct} % vollständig`,
          });
          onClose();
        },
        onError: (error) => toast.push({ kind: "error", title: "KPI konnte nicht gespeichert werden", body: errorText(error) }),
      },
    );
  }

  return (
    <Modal title={item ? "KPI-Definition bearbeiten" : "KPI-Definition anlegen"} onClose={onClose}>
      <form className="cs-form" onSubmit={submit}>
        <fieldset>
          <legend>Identität</legend>
          <div className="cs-form-grid cols-3">
            <label><span>Ebene</span><select value={form.layer} onChange={(e) => set("layer", e.target.value as KpiForm["layer"])}><option value="kunde">Kunde</option><option value="fachwissen">Fachwissen</option></select></label>
            <label><span>Technischer Schlüssel *</span><input value={form.metricKey} onChange={(e) => set("metricKey", e.target.value)} /></label>
            <label><span>Name *</span><input value={form.name} onChange={(e) => set("name", e.target.value)} /></label>
            <label className="span-3"><span>Definition</span><textarea rows={2} value={form.definition} onChange={(e) => set("definition", e.target.value)} /></label>
            <label className="span-2"><span>Formel</span><input value={form.formula} onChange={(e) => set("formula", e.target.value)} /></label>
            <label><span>Richtung</span><select value={form.direction} onChange={(e) => set("direction", e.target.value as KpiForm["direction"])}><option value="down_is_favorable">Niedriger ist besser</option><option value="up_is_favorable">Höher ist besser</option><option value="neutral">Sollwert treffen</option></select></label>
            <label><span>Einheit</span><input value={form.unit} onChange={(e) => set("unit", e.target.value)} /></label>
            <label><span>Zeitraum</span><input value={form.period} onChange={(e) => set("period", e.target.value)} /></label>
            <label><span>Status</span><select value={form.status} onChange={(e) => set("status", e.target.value)}><option value="entwurf">Entwurf</option><option value="zu_bestaetigen">Zu bestätigen</option><option value="freigegeben">Freigegeben</option></select></label>
          </div>
        </fieldset>
        <fieldset>
          <legend>Datenvertrag</legend>
          <div className="cs-form-grid cols-2">
            <label><span>Dimensionen</span><textarea rows={2} value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} /></label>
            <label><span>Benötigte Daten</span><textarea rows={2} value={form.requiredData} onChange={(e) => set("requiredData", e.target.value)} /></label>
            <label className="span-2"><span>Datenquellen</span><textarea rows={2} value={form.sourceRefs} onChange={(e) => set("sourceRefs", e.target.value)} /></label>
          </div>
        </fieldset>
        <fieldset>
          <legend>Ziel, Schwellen und Vergleich</legend>
          <div className="cs-form-grid cols-3">
            <label><span>Zielwert</span><input inputMode="decimal" value={form.target} onChange={(e) => set("target", e.target.value)} /></label>
            <label><span>Toleranz %</span><input inputMode="decimal" value={form.tolerance} onChange={(e) => set("tolerance", e.target.value)} /></label>
            <span />
            <label><span>Warnung %</span><input inputMode="decimal" value={form.warning} onChange={(e) => set("warning", e.target.value)} /></label>
            <label><span>Kritisch %</span><input inputMode="decimal" value={form.critical} onChange={(e) => set("critical", e.target.value)} /></label>
            <span />
            <label><span>Vergleichswert</span><input inputMode="decimal" value={form.benchmark} onChange={(e) => set("benchmark", e.target.value)} /></label>
            <label className="span-2"><span>Vergleichsquelle</span><input value={form.benchmarkSource} onChange={(e) => set("benchmarkSource", e.target.value)} /></label>
          </div>
        </fieldset>
        <fieldset>
          <legend>Verantwortung und Nachweis</legend>
          <div className="cs-form-grid cols-3">
            <label><span>Verantwortlicher *</span><input value={form.owner} onChange={(e) => set("owner", e.target.value)} /></label>
            <label><span>Verlässlichkeit 0-1</span><input inputMode="decimal" value={form.confidence} onChange={(e) => set("confidence", e.target.value)} /></label>
            <label><span>Version</span><input value={String(item ? form.version + 1 : form.version)} disabled /></label>
            <label className="span-3"><span>Nachweise *</span><textarea rows={2} value={form.evidence} onChange={(e) => set("evidence", e.target.value)} /></label>
          </div>
        </fieldset>
        <footer className="cs-form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn primary" disabled={save.isPending}>{save.isPending ? "Speichert..." : "KPI speichern"}</button>
        </footer>
      </form>
    </Modal>
  );
}

function AdoptModal({ item, onClose }: { item: ControlPackage; onClose: () => void }) {
  const [owner, setOwner] = useState(item.owner ?? "");
  const [evidence, setEvidence] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const adopt = useAdoptControlPackage();
  const toast = useToast();

  function submit(event: FormEvent) {
    event.preventDefault();
    adopt.mutate(
      { id: item.id, input: { owner: owner.trim(), confirmed, evidence_refs: splitList(evidence) } },
      {
        onSuccess: () => {
          toast.push({ kind: "success", title: "Fachpaket übernommen", body: item.title });
          onClose();
        },
        onError: (error) => toast.push({ kind: "error", title: "Übernahme fehlgeschlagen", body: errorText(error) }),
      },
    );
  }

  return (
    <Modal title="Fachpaket für den Kunden übernehmen" onClose={onClose}>
      <form className="cs-form" onSubmit={submit}>
        <div className="cs-selection-summary"><Status value={PACKAGE_LABEL[item.package_type]} /><strong>{item.title}</strong><span>Version {item.version}</span></div>
        <div className="cs-form-grid cols-2">
          <label><span>Verantwortlicher</span><input value={owner} onChange={(e) => setOwner(e.target.value)} /></label>
          <label><span>Zusätzlicher Nachweis</span><input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="evidence://..." /></label>
        </div>
        <label className="cs-check"><input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} /> Kundenspezifische Ausprägung fachlich bestätigt</label>
        <footer className="cs-form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn primary" disabled={adopt.isPending}>{adopt.isPending ? "Übernimmt..." : "Übernehmen"}</button>
        </footer>
      </form>
    </Modal>
  );
}

function IntakeRow({ item, canWrite }: { item: IntakeItem; canWrite: boolean }) {
  const [value, setValue] = useState(() => displayValue(item.value ?? item.proposal));
  const [evidence, setEvidence] = useState(() => item.evidence_refs.join("\n"));
  const update = useUpdateControlIntake();
  const toast = useToast();
  const needsHuman = item.mode === "human_decision";

  function save() {
    const refs = splitList(evidence);
    if (needsHuman && refs.length === 0) {
      toast.push({ kind: "warn", title: "Nachweis erforderlich" });
      return;
    }
    update.mutate(
      {
        id: item.id,
        input: {
          value: value.trim(),
          status: needsHuman ? "entschieden" : "bestaetigt",
          evidence_refs: refs,
        },
      },
      {
        onSuccess: () => toast.push({ kind: "success", title: needsHuman ? "Entscheidung gespeichert" : "Vorschlag bestätigt" }),
        onError: (error) => toast.push({ kind: "error", title: "Speichern fehlgeschlagen", body: errorText(error) }),
      },
    );
  }

  return (
    <div className={`cs-intake-row mode-${item.mode}`}>
      <div className="cs-intake-question">
        <Status value={MODE_LABEL[item.mode] ?? item.mode} tone={item.mode} />
        <strong>{item.question}</strong>
        <span>{confidence(item.confidence)} Verlässlichkeit · {item.owner || "ohne Verantwortung"}</span>
      </div>
      <div className="cs-intake-value">
        <input value={value} onChange={(event) => setValue(event.target.value)} disabled={!canWrite || item.mode === "automatic"} aria-label={`Wert: ${item.question}`} />
        <input value={evidence} onChange={(event) => setEvidence(event.target.value)} disabled={!canWrite || item.mode === "automatic"} placeholder="Nachweis" aria-label={`Nachweis: ${item.question}`} />
      </div>
      <div className="cs-intake-action">
        <Evidence refs={item.evidence_refs} />
        {canWrite && item.mode !== "automatic" && <button type="button" className="btn small" onClick={save} disabled={update.isPending}>Speichern</button>}
      </div>
    </div>
  );
}

function ObjectRow({ item }: { item: ControlObject }) {
  return (
    <tr>
      <td><span className="cs-object-index">{item.iteration}.{Math.round(item.sequence / 10)}</span></td>
      <td><strong>{OBJECT_LABEL[item.object_type] ?? item.object_type}</strong><small>{item.title}</small></td>
      <td><Status value={item.status} /></td>
      <td><span>{item.owner || "-"}</span><small>{confidence(item.confidence)} · {item.generated_by}</small></td>
      <td><Evidence refs={item.evidence_refs} /></td>
      <td>{item.human_required ? <Status value="Prüfung" tone="high" /> : <span className="cs-auto">Automatisch</span>}</td>
    </tr>
  );
}

function CaseDetailPanel({ detail, stages, canWrite }: { detail: ControlCaseDetail; stages: Array<{ key: string; label: string }>; canWrite: boolean }) {
  const run = useRunControlCase();
  const decide = useDecideControlCase();
  const impact = useMeasureControlImpact();
  const toast = useToast();
  const [rationale, setRationale] = useState("");
  const [decisionEvidence, setDecisionEvidence] = useState("");
  const [actionOwner, setActionOwner] = useState(detail.owner ?? "");
  const [expectedImpact, setExpectedImpact] = useState("");
  const [impactValue, setImpactValue] = useState("");
  const [impactPeriod, setImpactPeriod] = useState("");
  const [impactEvidence, setImpactEvidence] = useState("");
  const [lesson, setLesson] = useState("");

  useEffect(() => {
    setRationale("");
    setDecisionEvidence("");
    setActionOwner(detail.owner ?? "");
    setExpectedImpact("");
    setImpactValue("");
    setImpactPeriod("");
    setImpactEvidence("");
    setLesson("");
  }, [detail.id]);

  function rerun() {
    run.mutate(detail.id, {
      onSuccess: () => toast.push({ kind: "success", title: "Automatisierung abgeschlossen" }),
      onError: (error) => toast.push({ kind: "error", title: "Automatisierung fehlgeschlagen", body: errorText(error) }),
    });
  }

  function submitDecision(choice: "approved" | "rejected" | "deferred") {
    const refs = splitList(decisionEvidence);
    if (!rationale.trim() || (detail.scenario_type !== "synthetic_reference" && refs.length === 0)) {
      toast.push({ kind: "warn", title: "Begründung und Nachweis erforderlich" });
      return;
    }
    decide.mutate(
      {
        id: detail.id,
        input: {
          decision: choice,
          rationale: rationale.trim(),
          evidence_refs: refs,
          action_owner: actionOwner.trim(),
          expected_impact_pct: numberOrUndefined(expectedImpact),
        },
      },
      {
        onSuccess: () => toast.push({ kind: "success", title: "Entscheidung protokolliert" }),
        onError: (error) => toast.push({ kind: "error", title: "Entscheidung fehlgeschlagen", body: errorText(error) }),
      },
    );
  }

  function submitImpact(event: FormEvent) {
    event.preventDefault();
    const refs = splitList(impactEvidence);
    if (numberOrUndefined(impactValue) == null || refs.length === 0) {
      toast.push({ kind: "warn", title: "Messwert und Wirkungsnachweis erforderlich" });
      return;
    }
    impact.mutate(
      {
        id: detail.id,
        input: {
          value: numberOrUndefined(impactValue),
          period: impactPeriod.trim(),
          expected_improvement_pct: numberOrUndefined(expectedImpact),
          evidence_refs: refs,
          lesson: lesson.trim(),
        },
      },
      {
        onSuccess: () => toast.push({ kind: "success", title: "Wirkung gemessen", body: "Der nächste Messzyklus wurde angelegt." }),
        onError: (error) => toast.push({ kind: "error", title: "Wirkung konnte nicht gespeichert werden", body: errorText(error) }),
      },
    );
  }

  const openDecision = detail.decision_items.some((item) => item.trigger_key === "decision_required" && item.status !== "erledigt");
  const activeAction = detail.objects.some((item) => item.object_type === "massnahme" && item.status !== "abgeschlossen");

  return (
    <section className="cs-case-detail">
      <header className="cs-case-head">
        <div>
          <div className="cs-inline-status"><Status value={detail.status} /><Status value={detail.scenario_type === "live" ? "Live" : "Referenz"} tone={detail.scenario_type} /></div>
          <h2>{detail.title}</h2>
          <p>{detail.owner || "Ohne Verantwortung"} · Aktualisiert {formatDate(detail.updated_at)} · Iteration {detail.iteration}</p>
        </div>
        {canWrite && ["entwurf", "beratung_erforderlich"].includes(detail.status) && (
          <button type="button" className="btn primary" onClick={rerun} disabled={run.isPending}>{run.isPending ? "Prüft..." : "Automatisierung ausführen"}</button>
        )}
      </header>

      <StageStrip detail={detail} stages={stages} />

      <div className="cs-facts">
        <div><span>KPI</span><strong>{detail.kpi_definition?.name ?? "Nicht zugeordnet"}</strong><small>{detail.kpi_definition?.completeness?.score_pct ?? 0} % vollständig</small></div>
        <div><span>Datenquelle</span><strong>{detail.source_ref || "Nicht hinterlegt"}</strong><Evidence refs={detail.evidence_refs} /></div>
        <div><span>Kette</span><strong>{detail.chain_valid ? "Gültig" : "Prüfung erforderlich"}</strong><small>{detail.objects.length} Objekte · {detail.links.length} Beziehungen</small></div>
        <div><span>Verlässlichkeit</span><strong>{confidence(detail.confidence)}</strong><small>{detail.decision_items.filter((item) => item.status !== "erledigt").length} offene Übergaben</small></div>
      </div>

      {canWrite && openDecision && (
        <section className="cs-work-panel decision">
          <header><div><span>Entscheidung erforderlich</span><h3>Potenzial und Annahmen fachlich bewerten</h3></div><Status value="Menschlich" tone="human_decision" /></header>
          <div className="cs-form-grid cols-2">
            <label className="span-2"><span>Begründung *</span><textarea rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} /></label>
            <label><span>Entscheidungsnachweis *</span><input value={decisionEvidence} onChange={(e) => setDecisionEvidence(e.target.value)} placeholder="evidence://..." /></label>
            <label><span>Maßnahmenverantwortung</span><input value={actionOwner} onChange={(e) => setActionOwner(e.target.value)} /></label>
            <label><span>Erwartete Verbesserung %</span><input inputMode="decimal" value={expectedImpact} onChange={(e) => setExpectedImpact(e.target.value)} /></label>
          </div>
          <footer>
            <button type="button" className="btn ghost" onClick={() => submitDecision("deferred")} disabled={decide.isPending}>Vertagen</button>
            <button type="button" className="btn danger" onClick={() => submitDecision("rejected")} disabled={decide.isPending}>Ablehnen</button>
            <button type="button" className="btn primary" onClick={() => submitDecision("approved")} disabled={decide.isPending}>Freigeben</button>
          </footer>
        </section>
      )}

      {canWrite && activeAction && (
        <form className="cs-work-panel impact" onSubmit={submitImpact}>
          <header><div><span>Wirkungsmessung</span><h3>Ergebnis der Maßnahme</h3></div><Status value="Nachweis" tone="suggested" /></header>
          <div className="cs-form-grid cols-3">
            <label><span>Neuer Messwert *</span><input inputMode="decimal" value={impactValue} onChange={(e) => setImpactValue(e.target.value)} /></label>
            <label><span>Messzeitraum</span><input value={impactPeriod} onChange={(e) => setImpactPeriod(e.target.value)} /></label>
            <label><span>Erwartete Verbesserung %</span><input inputMode="decimal" value={expectedImpact} onChange={(e) => setExpectedImpact(e.target.value)} /></label>
            <label className="span-2"><span>Wirkungsnachweis *</span><input value={impactEvidence} onChange={(e) => setImpactEvidence(e.target.value)} placeholder="evidence://..." /></label>
            <label><span>Lernergebnis</span><input value={lesson} onChange={(e) => setLesson(e.target.value)} /></label>
          </div>
          <footer><button type="submit" className="btn primary" disabled={impact.isPending}>{impact.isPending ? "Speichert..." : "Wirkung speichern"}</button></footer>
        </form>
      )}

      {detail.decision_items.length > 0 && (
        <section className="cs-section-block">
          <header><h3>Beraterübergaben</h3><span>{detail.decision_items.filter((item) => item.status !== "erledigt").length} offen</span></header>
          <div className="cs-handoff-list">
            {detail.decision_items.map((item) => (
              <div key={item.id} className={`priority-${item.priority}`}>
                <Status value={item.priority} tone={item.priority} />
                <div><strong>{item.title}</strong><p>{item.reason}</p></div>
                <div><Status value={item.status} /><Evidence refs={item.evidence_refs} /></div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="cs-section-block">
        <header><h3>Prüfpfad</h3><span>{detail.objects.length} strukturierte Objekte</span></header>
        <div className="cs-table-wrap">
          <table className="cs-table cs-object-table">
            <thead><tr><th>Schritt</th><th>Objekt</th><th>Status</th><th>Verantwortung</th><th>Nachweis</th><th>Modus</th></tr></thead>
            <tbody>{detail.objects.map((item) => <ObjectRow key={item.id} item={item} />)}</tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

export default function ControlSystemPage() {
  const { roles, mode } = useAuth();
  const { effectiveTenantId } = useTenant();
  const queryClient = useQueryClient();
  const toast = useToast();
  const canWrite = mode === "demo" || roles.some((role) => ["platform_admin", "tenant_admin", "berater", "advisor", "consultant"].includes(role));
  const [tab, setTab] = useState<Tab>("today");
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [editingKpi, setEditingKpi] = useState<KpiDefinition | null | undefined>(undefined);
  const [adopting, setAdopting] = useState<ControlPackage | null>(null);
  const [packageFilter, setPackageFilter] = useState<PackageType | "all">("all");
  const [intakeForm, setIntakeForm] = useState({ company: "", industry: "", processes: "", sources: "", quality: "", owner: "" });

  const contract = useControlContract();
  const overview = useControlOverview();
  const packages = useControlPackages();
  const kpis = useControlKpis();
  const intake = useControlIntake();
  const cases = useControlCases(true);
  const decisions = useControlDecisions("open");
  const detail = useControlCase(selectedCaseId);
  const derive = useDeriveControlIntake();
  const reference = useCreateReferenceCase();

  useEffect(() => {
    if (selectedCaseId) return;
    const first = decisions.data?.[0]?.case_id ?? cases.data?.[0]?.id;
    if (first) setSelectedCaseId(first);
  }, [cases.data, decisions.data, selectedCaseId]);

  const filteredPackages = useMemo(
    () => (packages.data ?? []).filter((item) => packageFilter === "all" || item.package_type === packageFilter),
    [packageFilter, packages.data],
  );

  function openCase(id: string) {
    setSelectedCaseId(id);
    setTab("cases");
  }

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["control-system"] });
    toast.push({ kind: "info", title: "Daten werden aktualisiert" });
  }

  function deriveSetup(event: FormEvent) {
    event.preventDefault();
    derive.mutate(
      {
        company_name: intakeForm.company.trim() || undefined,
        industry: intakeForm.industry.trim() || undefined,
        processes: splitList(intakeForm.processes),
        sources: splitList(intakeForm.sources),
        data_quality: numberOrUndefined(intakeForm.quality),
        owner: intakeForm.owner.trim() || undefined,
      },
      {
        onSuccess: () => toast.push({ kind: "success", title: "Einrichtung abgeleitet" }),
        onError: (error) => toast.push({ kind: "error", title: "Ableitung fehlgeschlagen", body: errorText(error) }),
      },
    );
  }

  function createReference() {
    reference.mutate(true, {
      onSuccess: (result) => {
        setSelectedCaseId(result.id);
        setTab("cases");
        toast.push({
          kind: result.reference_verification?.complete ? "success" : "warn",
          title: result.reference_verification?.complete ? "Referenzfall vollständig" : "Referenzfall prüfen",
        });
      },
      onError: (error) => toast.push({ kind: "error", title: "Referenzfall fehlgeschlagen", body: errorText(error) }),
    });
  }

  const ov = overview.data;
  const stages = contract.data?.stages ?? [];
  const allCases = cases.data ?? [];

  return (
    <section className="cs-page" aria-label="Steuerungssystem">
      <header className="cs-page-head">
        <div>
          <p className="hero-eyebrow">CompanyBrain · geschlossener Kreislauf</p>
          <h1>Steuerungssystem</h1>
          <p>Kontext · Kennzahl · Abweichung · Entscheidung · Maßnahme · Wirkung · Lernen</p>
        </div>
        <div className="cs-head-actions">
          <span className={`cs-persist ${ov?.persistent ? "on" : "off"}`}>{ov?.persistent ? "Postgres aktiv" : "Temporärer Speicher"}</span>
          <button type="button" className="cs-icon-btn" onClick={refresh} title="Aktualisieren" aria-label="Aktualisieren">↻</button>
          {canWrite && <button type="button" className="btn primary" onClick={() => setShowCaseModal(true)}>Fall anlegen</button>}
        </div>
      </header>

      <div className="cs-stat-band">
        <button type="button" onClick={() => setTab("today")}><span>Offene Entscheidungen</span><strong>{ov?.decision_queue.open ?? 0}</strong><small>{ov?.decision_queue.critical ?? 0} kritisch · {ov?.decision_queue.high ?? 0} hoch</small></button>
        <button type="button" onClick={() => setTab("cases")}><span>Steuerungsfälle</span><strong>{ov?.cases.live ?? 0}</strong><small>{ov?.cases.reference ?? 0} Referenz</small></button>
        <button type="button" onClick={() => setTab("kpis")}><span>Vollständige KPIs</span><strong>{ov?.kpis.complete ?? 0}/{ov?.kpis.total ?? 0}</strong><small>{ov?.kpis.incomplete ?? 0} unvollständig</small></button>
        <button type="button" onClick={() => setTab("packages")}><span>Fachpakete</span><strong>{ov?.packages.total ?? 0}</strong><small>{ov?.packages.customer_adoptions ?? 0} kundenspezifisch</small></button>
        <div><span>Schreibschutz</span><strong>{ov?.external_writes === false ? "Aktiv" : "Prüfen"}</strong><small>Gold und externe Systeme read-only</small></div>
      </div>

      <div className="cs-tabs" role="tablist" aria-label="Ansichten">
        {(Object.keys(TAB_LABEL) as Tab[]).map((key) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? "active" : ""} onClick={() => setTab(key)}>
            {TAB_LABEL[key]}
            {key === "today" && (ov?.decision_queue.open ?? 0) > 0 && <span>{ov?.decision_queue.open}</span>}
          </button>
        ))}
      </div>

      {tab === "today" && (
        <div className="cs-view">
          <section className="cs-section-block">
            <header><h2>Priorisierte Entscheidungsliste</h2><span>{decisions.data?.length ?? 0} offen</span></header>
            {decisions.isLoading ? <LoadingState /> : (decisions.data?.length ?? 0) === 0 ? <EmptyState>Keine offenen Beraterübergaben.</EmptyState> : (
              <div className="cs-table-wrap">
                <table className="cs-table cs-decision-table">
                  <thead><tr><th>Priorität</th><th>Entscheidungspunkt</th><th>Verantwortung</th><th>Nachweis</th><th>Aktion</th></tr></thead>
                  <tbody>{decisions.data?.map((item) => <DecisionRow key={item.id} item={item} canWrite={canWrite} onOpen={openCase} />)}</tbody>
                </table>
              </div>
            )}
          </section>

          <section className="cs-section-block">
            <header><h2>Laufende Steuerungsfälle</h2><span>{allCases.filter((item) => item.scenario_type === "live").length} live</span></header>
            {cases.isLoading ? <LoadingState /> : allCases.length === 0 ? <EmptyState>Noch keine Steuerungsfälle.</EmptyState> : (
              <div className="cs-case-list compact">
                {allCases.slice(0, 10).map((item) => (
                  <button key={item.id} type="button" onClick={() => openCase(item.id)}>
                    <div><Status value={item.status} /><Status value={item.scenario_type === "live" ? "Live" : "Referenz"} tone={item.scenario_type} /></div>
                    <strong>{item.title}</strong>
                    <span>{item.owner || "Nicht zugewiesen"}</span>
                    <small>{item.current_stage.replace(/_/g, " ")} · {formatDate(item.updated_at)}</small>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {tab === "cases" && (
        <div className="cs-cases-layout">
          <aside className="cs-case-sidebar">
            <header><h2>Fälle</h2><span>{allCases.length}</span></header>
            {canWrite && effectiveTenantId === "tnt_demo" && (
              <button type="button" className="btn ghost small cs-reference-btn" onClick={createReference} disabled={reference.isPending}>{reference.isPending ? "Prüft..." : "Referenzfall prüfen"}</button>
            )}
            <div className="cs-case-list">
              {allCases.map((item: ControlCase) => (
                <button key={item.id} type="button" className={selectedCaseId === item.id ? "active" : ""} onClick={() => setSelectedCaseId(item.id)}>
                  <div><Status value={item.status} /><span>{confidence(item.confidence)}</span></div>
                  <strong>{item.title}</strong>
                  <small>{item.owner || "Nicht zugewiesen"} · {formatDate(item.updated_at)}</small>
                </button>
              ))}
              {!cases.isLoading && allCases.length === 0 && <EmptyState>Noch keine Fälle.</EmptyState>}
            </div>
          </aside>
          <div className="cs-case-main">
            {detail.isLoading ? <LoadingState label="Steuerungsfall wird geladen" /> : detail.data ? (
              <CaseDetailPanel detail={detail.data} stages={stages} canWrite={canWrite} />
            ) : <EmptyState>Steuerungsfall auswählen.</EmptyState>}
          </div>
        </div>
      )}

      {tab === "packages" && (
        <div className="cs-view">
          <div className="cs-toolbar">
            <div className="cs-segments" role="group" aria-label="Pakettyp filtern">
              <button type="button" className={packageFilter === "all" ? "active" : ""} onClick={() => setPackageFilter("all")}>Alle</button>
              {(Object.keys(PACKAGE_LABEL) as PackageType[]).map((type) => <button key={type} type="button" className={packageFilter === type ? "active" : ""} onClick={() => setPackageFilter(type)}>{PACKAGE_LABEL[type]}</button>)}
            </div>
            <span>{filteredPackages.length} Pakete</span>
          </div>
          <section className="cs-section-block">
            <header><h2>Branchen-, Prozess-, KPI- und Anwendungsfallpakete</h2><span>{ov?.packages.customer_adoptions ?? 0} übernommen</span></header>
            {packages.isLoading ? <LoadingState /> : (
              <div className="cs-table-wrap">
                <table className="cs-table">
                  <thead><tr><th>Typ</th><th>Paket</th><th>Ebene</th><th>Status</th><th>Verantwortung</th><th>Nachweis</th><th /></tr></thead>
                  <tbody>{filteredPackages.map((item) => (
                    <tr key={item.id}>
                      <td><Status value={PACKAGE_LABEL[item.package_type]} /></td>
                      <td><strong>{item.title}</strong><p>{item.summary}</p><small>Version {item.version} · {confidence(item.confidence)}</small></td>
                      <td><Status value={item.layer} tone={item.layer} /></td>
                      <td><Status value={item.status} /></td>
                      <td>{item.owner || "-"}</td>
                      <td><Evidence refs={item.evidence_refs} /></td>
                      <td className="cs-table-actions">{canWrite && item.layer === "fachwissen" && <button type="button" className="btn small" onClick={() => setAdopting(item)}>Übernehmen</button>}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {tab === "kpis" && (
        <div className="cs-view">
          <div className="cs-toolbar">
            <div><strong>{ov?.kpis.complete ?? 0}</strong> vollständig <span>·</span> <strong>{ov?.kpis.incomplete ?? 0}</strong> unvollständig</div>
            {canWrite && <button type="button" className="btn primary" onClick={() => setEditingKpi(null)}>KPI anlegen</button>}
          </div>
          <section className="cs-section-block">
            <header><h2>KPI-Definitionen</h2><span>Definition und Messwerte getrennt</span></header>
            {kpis.isLoading ? <LoadingState /> : (kpis.data?.length ?? 0) === 0 ? <EmptyState>Noch keine KPI-Definitionen.</EmptyState> : (
              <div className="cs-table-wrap">
                <table className="cs-table cs-kpi-table">
                  <thead><tr><th>Kennzahl</th><th>Vollständigkeit</th><th>Ziel und Schwellen</th><th>Verantwortung</th><th>Nachweis</th><th /></tr></thead>
                  <tbody>{kpis.data?.map((item) => (
                    <tr key={item.id}>
                      <td><strong>{item.name}</strong><p>{item.definition || "Definition fehlt"}</p><small>{item.metric_key} · Version {item.version} · {item.layer}</small></td>
                      <td><div className="cs-completeness"><span><i style={{ width: `${item.completeness.score_pct}%` }} /></span><strong>{item.completeness.score_pct} %</strong></div>{!item.completeness.complete && <small>{item.completeness.missing.map((missing) => missing.label).join(", ")}</small>}</td>
                      <td><span>Ziel {displayValue(item.target?.value)} {item.unit}</span><small>Warnung {displayValue(item.warning_thresholds?.warning_pct)} % · kritisch {displayValue(item.warning_thresholds?.critical_pct)} %</small></td>
                      <td><span>{item.owner || "-"}</span><small>{confidence(item.confidence)} · {item.status}</small></td>
                      <td><Evidence refs={item.evidence_refs} /></td>
                      <td className="cs-table-actions">{canWrite && <button type="button" className="btn small" onClick={() => setEditingKpi(item)}>Bearbeiten</button>}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {tab === "setup" && (
        <div className="cs-view">
          <section className="cs-layer-band">
            {(ov?.layers ?? contract.data?.layers ?? []).map((layer) => (
              <div key={layer.key} className={`layer-${layer.key}`}>
                <span>{layer.label}</span>
                <strong>{layer.object_count ?? 0}</strong>
                <small>{layer.purpose}</small>
              </div>
            ))}
          </section>

          {canWrite && (
            <form className="cs-derive" onSubmit={deriveSetup}>
              <header><h2>Einrichtung ableiten</h2><span>Bestehende Mandantendaten werden einbezogen</span></header>
              <div className="cs-form-grid cols-3">
                <label><span>Unternehmen</span><input value={intakeForm.company} onChange={(e) => setIntakeForm({ ...intakeForm, company: e.target.value })} /></label>
                <label><span>Branche</span><input value={intakeForm.industry} onChange={(e) => setIntakeForm({ ...intakeForm, industry: e.target.value })} /></label>
                <label><span>Verantwortlicher</span><input value={intakeForm.owner} onChange={(e) => setIntakeForm({ ...intakeForm, owner: e.target.value })} /></label>
                <label><span>Prozesse</span><input value={intakeForm.processes} onChange={(e) => setIntakeForm({ ...intakeForm, processes: e.target.value })} /></label>
                <label><span>Datenquellen</span><input value={intakeForm.sources} onChange={(e) => setIntakeForm({ ...intakeForm, sources: e.target.value })} /></label>
                <label><span>Datenqualität 0-1</span><input inputMode="decimal" value={intakeForm.quality} onChange={(e) => setIntakeForm({ ...intakeForm, quality: e.target.value })} /></label>
              </div>
              <footer><button type="submit" className="btn primary" disabled={derive.isPending}>{derive.isPending ? "Leitet ab..." : "Aus Bestand ableiten"}</button></footer>
            </form>
          )}

          <section className="cs-section-block">
            <header><h2>Einrichtungspunkte</h2><span>{intake.data?.length ?? 0}</span></header>
            {intake.isLoading ? <LoadingState /> : (intake.data?.length ?? 0) === 0 ? <EmptyState>Noch keine Einrichtung abgeleitet.</EmptyState> : (
              <div className="cs-intake-list">{intake.data?.map((item: IntakeItem) => <IntakeRow key={item.id} item={item} canWrite={canWrite} />)}</div>
            )}
          </section>
        </div>
      )}

      {showCaseModal && <CaseModal packages={packages.data ?? []} kpis={kpis.data ?? []} onClose={() => setShowCaseModal(false)} onCreated={setSelectedCaseId} />}
      {editingKpi !== undefined && <KpiModal item={editingKpi} onClose={() => setEditingKpi(undefined)} />}
      {adopting && <AdoptModal item={adopting} onClose={() => setAdopting(null)} />}
    </section>
  );
}
